/**
 * Input step editor v2 — the switch (Ram, 2026-10-21).
 *
 * v2 is the DEFAULT for C (Ram approved, 2026-10-21). The old Input property
 * editor stays reachable behind the switch:
 *   - `?inputEditor=old` in the URL (remembered in sessionStorage for the tab,
 *     so moving around the app keeps it; `?inputEditor=new` turns v2 back on), or
 *   - `REACT_APP_INPUT_EDITOR=old` at build time.
 * Only C gets v2: every other language always gets the old editor.
 *
 * The rest of v2 follows the same switch: the "More options" disclosure for
 * Label / Description, the hidden footer and the empty-step message. With the
 * switch set to old, all of those are as they were before v2.
 *
 * The Output step's v2 editor (the message with value chips, OutputStepEditorV2)
 * is on the SAME switch (Ram, 2026-10-21): the name says Input for history; it is
 * the switch for every v2 step editor. `?inputEditor=old` brings back all the old
 * editors at once.
 *
 * The Assignment step's v2 editor (sentence rows that read "Set total to
 * price × units", AssignmentStepEditorV2) is on it too (Ram, 2026-10-22).
 */
import useConfigStore from '@/stores/useConfigStore';

export const INPUT_EDITOR_STORAGE_KEY = 'f2c.inputEditor';
export const INPUT_EDITOR_PARAM = 'inputEditor';

const safeStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
};

/**
 * Is v2 on? Reads the URL first (and remembers it), then the session, then the
 * build-time env; on unless one of them says old. Never throws.
 */
export function isInputEditorV2On() {
  const storage = safeStorage();
  try {
    const search = typeof window !== 'undefined' ? window.location?.search || '' : '';
    const fromUrl = new URLSearchParams(search).get(INPUT_EDITOR_PARAM);
    if (fromUrl === 'new' || fromUrl === 'old') {
      try { storage?.setItem(INPUT_EDITOR_STORAGE_KEY, fromUrl); } catch (e) { /* private mode */ }
      return fromUrl === 'new';
    }
  } catch (e) { /* no URL: fall through */ }
  try {
    const remembered = storage?.getItem(INPUT_EDITOR_STORAGE_KEY);
    if (remembered === 'old') return false;
    if (remembered === 'new') return true;
  } catch (e) { /* blocked storage: fall through */ }
  return process.env.REACT_APP_INPUT_EDITOR !== 'old';
}

/** The switch, for a given language: v2 only for C. */
export function inputEditorV2For(language) {
  return (language || 'c') === 'c' && isInputEditorV2On();
}

/** Non-hook read, for validators and callbacks: the switch for the current language. */
export function isInputEditorV2() {
  let language = 'c';
  try { language = useConfigStore.getState().language || 'c'; } catch (e) { /* default c */ }
  return inputEditorV2For(language);
}

/** Hook: is the new Input editor (and the rest of v2) on for this panel? On by default for C. */
export function useInputEditorV2() {
  const language = useConfigStore((s) => s.language) || 'c';
  return inputEditorV2For(language);
}

export const EMPTY_INPUT_MESSAGE_OLD = 'This step asks the user for a value. Add one to get started.';
export const EMPTY_INPUT_MESSAGE_V2 = "This step doesn't read anything yet.";

/**
 * What the step's red mark and the Problems list say for an Input that reads
 * nothing. Both validators (nodeValidationHelpers.js and utils/nodeValidation.js)
 * ask here, so the one message follows the switch.
 */
export function emptyInputMessage() {
  return isInputEditorV2() ? EMPTY_INPUT_MESSAGE_V2 : EMPTY_INPUT_MESSAGE_OLD;
}

export const EMPTY_OUTPUT_MESSAGE_OLD_HELPERS = 'Output message is empty. Add text or variables to display.';
export const EMPTY_OUTPUT_MESSAGE_OLD = 'Output message or template is required';
export const EMPTY_OUTPUT_MESSAGE_V2 = "This step doesn't show anything yet";

/**
 * What the Run check and the Problems list say for an Output step with no
 * message. v2 has its own wording; with the switch old, each validator keeps
 * the words it always had (`old` is that validator's own message).
 */
export function emptyOutputMessage(old) {
  return isInputEditorV2() ? EMPTY_OUTPUT_MESSAGE_V2 : old;
}

export const EMPTY_ASSIGNMENT_MESSAGE_OLD_HELPERS = 'No assignments defined. Add at least one variable assignment.';
export const EMPTY_ASSIGNMENT_MESSAGE_OLD = 'At least one assignment is required';
export const EMPTY_ASSIGNMENT_MESSAGE_V2 = "This step doesn't set anything yet";

/**
 * What the Run check and the Problems list say for an Assignment step that sets
 * nothing. The Assignment v2 editor (AssignmentStepEditorV2) is on this same
 * switch; with it old, each validator keeps its own words (`old`).
 */
export function emptyAssignmentMessage(old) {
  return isInputEditorV2() ? EMPTY_ASSIGNMENT_MESSAGE_V2 : old;
}

/**
 * The "Give back" (Return) and "Use your function" (Subflow) v2 editors are on
 * this switch too (Ram, 2026-10-23). These are what the step's red mark and the
 * Problems list say in v2; with the switch old each returns null and the
 * validators say what they always said.
 */
/** A Return that gives back nothing from a function that must give something: "Say what hra gives back" */
export function emptyReturnMessage(fnName) {
  return isInputEditorV2() ? `Say what ${fnName || 'this function'} gives back` : null;
}
/** A Return with a value in a function that gives back nothing. */
export function voidReturnValueMessage(fnName) {
  return isInputEditorV2()
    ? `${fnName || 'This function'} gives back nothing, so this step can't give a value back. Take the value out.`
    : null;
}
export const NO_FUNCTION_MESSAGE_V2 = 'Choose which of your flows to use.';
export const GONE_FUNCTION_MESSAGE_V2 = 'This flow no longer exists. Choose another.';
/** A parameter with nothing given for it: "Say what goes in count" */
export const missingArgumentMessage = (param) => `Say what goes in ${param}`;
